import InfoIcon from '@mui/icons-material/Info';
import {
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikCheckbox,
  FormikSelect,
  FormikTextField,
} from '../../../common/formik-material-ui';

const CampaignRegistration = ({ formik }) => {
  const attributeMap = [
    {
      value: 'subscriberOptin',
      viewValue: 'Subscriber Opt-In',
      tooltipText: 'Indicates whether you collect and process customer opt-ins',
      disabled: true,
    },
    {
      value: 'embeddedLink',
      viewValue: 'Embedded Link',
      tooltipText:
        'Indicates whether campaign messages will use an embedded link',
      disabled: false,
    },
    {
      value: 'subscriberOptout',
      viewValue: 'Subscriber Opt-Out',
      tooltipText:
        'Indicates whether you collect and process customer opt-outs',
      disabled: true,
    },
    {
      value: 'embeddedPhone',
      viewValue: 'Embedded Phone Number',
      tooltipText:
        'Indicates whether campaign messages will use an embedded phone number (other than the required HELP information contact phone number)',
      disabled: false,
    },
    {
      value: 'subscriberHelp',
      viewValue: 'Subscriber Help',
      tooltipText:
        'Indicates whether you have implemented message replies that tell customers how they can contact the message sender when they reply with the HELP keyword',
      disabled: true,
    },
    {
      value: 'affiliateMarketing',
      viewValue: 'Affiliate Marketing',
      tooltipText:
        'Indicates whether the campaign involves affiliate marketing',
      disabled: false,
    },
    {
      value: 'directLending',
      viewValue: 'Direct Lending or Loan Arrangement',
      tooltipText:
        'Indicates whether this campaign includes content related to direct lending or other loan arrangements',
      disabled: false,
    },
    {
      value: 'ageGated',
      viewValue: 'Age-gated Content',
      tooltipText:
        'Indicates whether the campaign includes age-gated content as defined by carrier and CTIA guidelines',
      disabled: false,
    },
  ];
  const usecases = [
    { viewValue: '2FA ($10 per month)', value: '2FA' },
    {
      viewValue: 'Account Notification ($10 per month)',
      value: 'ACCOUNT_NOTIFICATION',
    },
    {
      viewValue: 'Delivery Notification ($10 per month)',
      value: 'DELIVERY_NOTIFICATION',
    },
    { viewValue: 'Marketing ($10 per month)', value: 'MARKETING' },
    { viewValue: 'Customer Care ($10 per month)', value: 'CUSTOMER_CARE' },
    {
      viewValue: 'Fraud Alert Messaging ($10 per month)',
      value: 'FRAUD_ALERT',
    },
    {
      viewValue: 'Higher Education ($10 per month)',
      value: 'HIGHER_EDUCATION',
    },
    { viewValue: 'Polling Voting ($10 per month)', value: 'POLLING_VOTING' },
    {
      viewValue: 'Public Service Announcement ($10 per month)',
      value: 'PUBLIC_SERVICE_ANNOUNCEMENT ($10 per month)',
    },
    { viewValue: 'Security Alert ($10 per month)', value: 'SECURITY_ALERT' },
    { viewValue: 'Mixed ($10 per month)', value: 'MIXED' },
    { viewValue: 'Low Volume ($2 per month)', value: 'LOW_VOLUME' },
  ];
  const subUsecases = [
    { viewValue: '2FA', value: '2FA' },
    { viewValue: 'Account Notification', value: 'ACCOUNT_NOTIFICATION' },
    { viewValue: 'Customer Care', value: 'CUSTOMER_CARE' },
    { viewValue: 'Delivery Notification', value: 'DELIVERY_NOTIFICATION' },
    { viewValue: 'Fraud Alert Messaging', value: 'FRAUD_ALERT' },
    { viewValue: 'Higher Education', value: 'HIGHER_EDUCATION' },
    { viewValue: 'Low Volume', value: 'LOW_VOLUME' },
    { viewValue: 'Marketing', value: 'MARKETING' },
    { viewValue: 'Mixed', value: 'MIXED' },
    { viewValue: 'Polling Voting', value: 'POLLING_VOTING' },
    {
      viewValue: 'Public Service Announcement',
      value: 'PUBLIC_SERVICE_ANNOUNCEMENT',
    },
    { viewValue: 'Security Alert', value: 'SECURITY_ALERT' },
  ];
  const getUseCaseView = (val: string) => {
    return subUsecases.find((subUsecase) => {
      return subUsecase?.value === val;
    })?.viewValue;
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <FormikTextField
              name="campaign.campaignAlias"
              formik={formik}
              fullWidth
              label="Campaign Alias"
              size="small"
              required
              variant="outlined"
              helperText={'Add an alias for your campaign'}
            />
          </Grid>
          <div style={{ margin: '8px' }}>
            <LabelledOutline label="Campaign Type">
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <p>
                  Choose the option that you think best describes your use case
                  for SMS, even if it’s not an exact match. If “Low Volume” (the
                  cheapest) or “Mixed Campaign” are selected, Plivo has further
                  checkboxes that are required
                </p>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FormikSelect
                  name="campaign.usecase"
                  formik={formik}
                  fullWidth
                  label="Campaign Type"
                  size="small"
                  variant="outlined"
                  required
                >
                  {usecases.map((usecase) => (
                    <MenuItem key={usecase?.value} value={usecase?.value}>
                      {usecase?.viewValue}
                    </MenuItem>
                  ))}
                </FormikSelect>
                <div
                  style={{
                    color: 'rgba(0, 0, 0, 0.54)',
                    fontSize: '0.75rem',
                    margin: '4px 14px 0px',
                  }}
                >
                  <span>Select your campaign's use case.</span>
                </div>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {(formik?.values?.campaign?.usecase === 'STARTER' ||
                  formik?.values?.campaign?.usecase === 'MIXED' ||
                  formik?.values?.campaign?.usecase === 'LOW_VOLUME') && (
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <FormControl
                      required
                      error={
                        (formik?.values?.campaign?.usecase === 'MIXED' &&
                          Object.values(
                            formik?.values?.campaign?.subUsecases
                          ).filter((el) => el === true).length < 2) ||
                        (formik?.values?.campaign?.usecase === 'LOW_VOLUME' &&
                          Object.values(
                            formik?.values?.campaign?.subUsecases
                          ).filter((el) => el === true).length < 1)
                      }
                      component="fieldset"
                    >
                      <Grid container spacing={1}>
                        {Object.keys(formik?.values?.campaign?.subUsecases).map(
                          (keyName, i) => (
                            <Grid
                              key={keyName}
                              item
                              xs={12}
                              sm={6}
                              md={6}
                              lg={6}
                              xl={6}
                            >
                              <FormControlLabel
                                control={
                                  <FormikCheckbox
                                    color="primary"
                                    formik={formik}
                                    disabled={
                                      Object.values(
                                        formik?.values?.campaign?.subUsecases
                                      ).filter((el) => el === true).length ===
                                        5 &&
                                      formik?.values?.campaign?.subUsecases?.[
                                        keyName
                                      ] !== true
                                    }
                                    name={`campaign.subUsecases.${keyName}`}
                                  />
                                }
                                style={{
                                  minWidth: '200px',
                                  marginRight: '8px',
                                }}
                                label={
                                  keyName === 'F2A'
                                    ? '2FA'
                                    : getUseCaseView(keyName)
                                }
                              />
                            </Grid>
                          )
                        )}
                      </Grid>
                      {formik?.values?.campaign?.usecase === 'MIXED' ? (
                        <FormHelperText>
                          Must select 2 - 5 campaign sub-types
                        </FormHelperText>
                      ) : formik?.values?.campaign?.usecase === 'LOW_VOLUME' ? (
                        <FormHelperText>
                          Must select 1 - 5 campaign sub-types
                        </FormHelperText>
                      ) : (
                        <FormHelperText>
                          Please select a campaign sub-types
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </LabelledOutline>
          </div>

          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <FormikTextField
              name="campaign.description"
              formik={formik}
              fullWidth
              label="Campaign Description"
              size="small"
              required
              variant="outlined"
              multiline
              rows={4}
              helperText="Help carriers understand what message you will be sending"
              InputLabelProps={{ shrink: true }}
              placeholder="<Brand Name> uses this campaign to provide real-time alerts to customers regarding packages they ordered on <website name>"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <FormikTextField
              name="campaign.messageFlow"
              formik={formik}
              fullWidth
              label="SMS Opt-In Process Proof"
              size="small"
              required
              variant="outlined"
              multiline
              rows={4}
              InputLabelProps={{ shrink: true }}
              placeholder="Customer opts in by visiting www.website.com and adding their phone number. They then check a box agreeing to receive text messages from Brand."
            />
            <div
              style={{
                color: 'rgba(0, 0, 0, 0.54)',
                fontSize: '0.75rem',
                margin: '4px 14px 0px',
              }}
            >
              <span>
                Call to action that shows how a user finds out about the
                campaign. Please refer to our
              </span>{' '}
              <a
                rel="noreferrer"
                target="_blank"
                href="https://support.kixie.com/hc/en-us/articles/16188947660571"
              >
                Recommendations for SMS Opt-In Process article
              </a>{' '}
              <span>for tips.</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <FormikTextField
              name="campaign.sample1"
              formik={formik}
              fullWidth
              label="Sample Message #1"
              size="small"
              required
              variant="outlined"
              multiline
              rows={4}
              InputLabelProps={{ shrink: true }}
              placeholder={
                '"Hi John, this is Legal Company Name. Do you have time to discuss our product offering with me?"'
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <FormikTextField
              name="campaign.sample2"
              formik={formik}
              fullWidth
              label="Sample Message #2"
              size="small"
              required
              variant="outlined"
              multiline
              rows={4}
              InputLabelProps={{ shrink: true }}
              placeholder={
                '"Hi John, this is Legal Company Name. Your scheduled visit is coming up tomorrow 10/01. Text C to confirm. Reply STOP to unsubscribe from further messages"'
              }
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <LabelledOutline label="Campaign and Content Attributes">
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div>Please respond to the following based on your usage.</div>
            </Grid>
            {attributeMap?.map((attribute) => (
              <Grid
                key={attribute.value}
                item
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={6}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                    }}
                  >
                    <p style={{ marginTop: 10, marginBottom: 0 }}>
                      {attribute.viewValue}
                    </p>
                    <Tooltip
                      TransitionComponent={Fade}
                      placement="right-start"
                      TransitionProps={{ timeout: 600 }}
                      title={attribute?.tooltipText ?? ''}
                    >
                      <p style={{ margin: '8px 6px 0', cursor: 'pointer' }}>
                        <InfoIcon fontSize="small" />
                      </p>
                    </Tooltip>
                  </div>
                  <RadioGroup
                    row
                    name={`campaign.attributes.${attribute.value}`}
                    style={{ marginLeft: '13px' }}
                    value={
                      formik.values.campaign.attributes[attribute.value] ?? -1
                    }
                    onChange={(e) =>
                      formik.setFieldValue(
                        `campaign.attributes.${attribute.value}`,
                        Number((e.target as any).value)
                      )
                    }
                  >
                    <FormControlLabel
                      value={1}
                      control={<Radio color="primary" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value={0}
                      control={
                        <Radio color="primary" disabled={attribute?.disabled} />
                      }
                      label="No"
                    />
                  </RadioGroup>
                </div>
              </Grid>
            ))}
          </Grid>
        </LabelledOutline>
      </Grid>
    </Grid>
  );
};
export default CampaignRegistration;
